import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { TABS } from "@/constants/testIds";

/* ─── data ────────────────────────────────────────── */
const CANDIDATE_STEPS = [
  {
    t: "Join with your college code",
    d: "Enter your placement cell's invite code. Your batch, branch and CGPA connect automatically.",
    detail: "Wishire pre-fills your profile from your existing resume — work history, skills and education import in seconds.",
  },
  {
    t: "Get your roadmap",
    d: "A personalised 12-week plan for your target role — technical or non-technical.",
    detail: "Foundations, core skills, aptitude and a mandatory communication phase — calibrated to what you already know, checkable topic by topic.",
  },
  {
    t: "Train like the real exam",
    d: "AI voice interviews + proctored company-pattern tests (TCS NQT, Infosys, Wipro & more).",
    detail: "Fresh questions every attempt, sectional cutoffs, camera proctoring — and a 'how you spoke' report no friend will give you honestly.",
  },
  {
    t: "Watch your Wishire Score rise",
    d: "One explainable readiness score — resume, interviews, tests and communication.",
    detail: "Every practice session moves it. You always know exactly where you stand and what to fix next.",
  },
  {
    t: "Sit drives with proof",
    d: "Apply to campus drives, take drive assessments, and let your Score do the talking.",
    detail: "Your placement cell sees scored, proctored, verified readiness — so shortlists happen on merit, not guesswork.",
  },
];

const COLLEGE_STEPS = [
  {
    t: "Onboard your batch in a day",
    d: "Bulk-import students, share the invite code, add your logo — done.",
    detail: "Your logo appears on every student dashboard and every report from day one. Departments, policies and drives configure in one working session.",
  },
  {
    t: "See readiness before the drive",
    d: "A live Wishire Score for every student — branch-wise, with confidence ratings.",
    detail: "Know who's drive-ready and who's at risk months before recruiters arrive — not after the rejection list.",
  },
  {
    t: "Let the AI Officer work",
    d: "Daily briefings, student nudges, deadline alerts — an agent that acts, not just reports.",
    detail: "It drafts recruiter outreach, watches every drive deadline, and executes approved actions 24/7.",
  },
  {
    t: "Run drives with proctored assessments",
    d: "Attach a screening test to any drive — ranked, integrity-verified shortlists in one click.",
    detail: "Per-student unique papers, camera + AI proctoring, violation timelines, sectional cutoffs — the Mettl replacement, built in.",
  },
  {
    t: "Report with proof",
    d: "Honest placement % on the placeable base, NIRF/NAAC-format PDFs with your logo.",
    detail: "Offer policies enforced automatically at apply time. Numbers you can defend to management, NIRF and NAAC.",
  },
];

/* ─── animation config ─────────────────────────────── */
const STEP_DURATION = 2500;

const modeVariants = {
  enter: (dir) => ({
    opacity: 0,
    x: dir > 0 ? 40 : -40,
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.42, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir > 0 ? -40 : 40,
    transition: { duration: 0.3, ease: "easeIn" },
  }),
};

/* ─── connector line (desktop) ─────────────────────── */
function ConnectorLine({ activeStep, total }) {
  const pct = ((activeStep) / (total - 1)) * 100;
  return (
    <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-stone-200 z-0 mx-12">
      <motion.div
        className="h-full bg-[#C2410C] origin-left"
        animate={{ scaleX: pct / 100 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ transformOrigin: "left" }}
      />
    </div>
  );
}

/* ─── single step card ─────────────────────────────── */
function StepCard({ step, index, isActive, onClick }) {
  return (
    <motion.li
      onClick={onClick}
      layout
      className={`relative z-10 flex-1 cursor-pointer rounded-3xl border p-6 md:p-7 transition-colors duration-200 ${
        isActive
          ? "border-[#C2410C] bg-white shadow-[0_8px_40px_-16px_rgba(194,65,12,0.25)]"
          : "border-stone-200 bg-[#FDFBF7] hover:border-stone-300 hover:bg-white"
      }`}
      animate={{ opacity: 1, y: 0 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ delay: index * 0.07, duration: 0.45 }}
    >
      {/* Step number */}
      <motion.span
        className="font-display block leading-none select-none"
        style={{ fontSize: "clamp(40px, 6vw, 64px)", color: "#C2410C" }}
        animate={{ opacity: isActive ? 1 : 0.3 }}
        transition={{ duration: 0.25 }}
      >
        {String(index + 1).padStart(2, "0")}
      </motion.span>

      <h3 className="font-display text-xl mt-3 leading-snug">{step.t}</h3>
      <p className="mt-2 text-stone-600 text-sm leading-relaxed">{step.d}</p>

      {/* Expanded detail */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="overflow-hidden"
          >
            <p className="mt-3 pt-3 border-t border-stone-200 text-stone-700 text-sm leading-relaxed">
              {step.detail}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active indicator dot */}
      <AnimatePresence>
        {isActive && (
          <motion.span
            key="dot"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="absolute top-5 right-5 w-2 h-2 rounded-full bg-[#C2410C]"
          />
        )}
      </AnimatePresence>
    </motion.li>
  );
}

/* ─── main export ─────────────────────────────────── */
export default function HowItWorks() {
  const [mode, setMode] = useState("candidate");
  const [modeDir, setModeDir] = useState(1);
  const [activeStep, setActiveStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);
  const sectionInView = useInView(sectionRef, { once: false, margin: "-120px" });

  const steps = mode === "candidate" ? CANDIDATE_STEPS : COLLEGE_STEPS;

  const switchMode = useCallback((next) => {
    setModeDir(next === "company" ? 1 : -1);
    setMode(next);
    setActiveStep(0);
    setPaused(false);
  }, []);

  const handleStepClick = useCallback((i) => {
    setActiveStep(i);
    setPaused(true);
  }, []);

  // Auto-advance
  useEffect(() => {
    if (paused || !sectionInView) return;
    const id = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, STEP_DURATION);
    return () => clearInterval(id);
  }, [paused, sectionInView, steps.length]);

  // Reset auto-play when mode changes
  useEffect(() => {
    setActiveStep(0);
    setPaused(false);
  }, [mode]);

  return (
    <section className="section" ref={sectionRef}>
      <div className="container-x">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="overline">How it works</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
              Five steps.{" "}
              <span className="italic text-stone-500">
                From signup to signed offer.
              </span>
            </h2>
          </div>

          {/* Toggle */}
          <div className="inline-flex p-1 rounded-full border border-stone-300 bg-white shrink-0">
            <button
              type="button"
              data-testid={TABS.howCandidate}
              onClick={() => switchMode("candidate")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                mode === "candidate"
                  ? "bg-stone-900 text-stone-50"
                  : "text-stone-600 hover:text-stone-900"
              }`}
            >
              For students
            </button>
            <button
              type="button"
              data-testid={TABS.howCompany}
              onClick={() => switchMode("company")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                mode === "company"
                  ? "bg-stone-900 text-stone-50"
                  : "text-stone-600 hover:text-stone-900"
              }`}
            >
              For colleges
            </button>
          </div>
        </div>

        {/* Progress bar (mobile) */}
        <div className="md:hidden mb-6 h-1 bg-stone-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-[#C2410C] rounded-full"
            animate={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Steps */}
        <AnimatePresence mode="wait" custom={modeDir}>
          <motion.div
            key={mode}
            custom={modeDir}
            variants={modeVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            {/* Desktop: relative wrapper for connector line */}
            <div className="relative">
              <ConnectorLine activeStep={activeStep} total={steps.length} />

              <ol className="flex flex-col md:flex-row gap-4 md:gap-3">
                {steps.map((s, i) => (
                  <StepCard
                    key={i}
                    step={s}
                    index={i}
                    isActive={i === activeStep}
                    onClick={() => handleStepClick(i)}
                  />
                ))}
              </ol>
            </div>

            {/* Auto-play hint */}
            <div className="mt-6 flex items-center justify-center gap-3">
              {steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleStepClick(i)}
                  aria-label={`Go to step ${i + 1}`}
                  className="focus:outline-none"
                >
                  <motion.span
                    className="block rounded-full"
                    animate={{
                      width: i === activeStep ? 24 : 8,
                      backgroundColor: i === activeStep ? "#C2410C" : "#D6D3D1",
                    }}
                    style={{ height: 8 }}
                    transition={{ duration: 0.3 }}
                  />
                </button>
              ))}
              {paused && (
                <button
                  type="button"
                  onClick={() => setPaused(false)}
                  className="ml-2 font-mono text-[10px] uppercase tracking-widest text-stone-400 hover:text-stone-700 transition-colors"
                >
                  resume
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
